import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Scissors, ArrowRight, Check } from "lucide-react";

export default function EmbroideryPanel({ onViewMap = null }) {
  const { eventId } = useMotif();
  const [reqs, setReqs] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/experiences/requests");
      setReqs(data);
    } catch (e) {}
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "experience_request_new" || msg.type === "experience_request_update" || msg.type === "ws_reconnected") refresh();
  });

  const update = async (id, status) => {
    try { await api.put(`/experiences/requests/${id}/status`, { status }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  // Current 3-state workflow: pending → in_progress → completed
  const next = { pending: "in_progress", in_progress: "completed" };
  const statusOf = (r) => (r.status || "").toLowerCase();

  const filtered = reqs.filter((r) => {
    const s = statusOf(r);
    if (filter === "active" && (s === "completed" || s === "cancelled")) return false;
    if (search && !r.guest_name.toLowerCase().includes(search.toLowerCase()) && !(r.text || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const STATUSES = [
    { id: "pending", label: "Pending" },
    { id: "in_progress", label: "In Progress" },
    { id: "completed", label: "Completed" },
  ];
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s.id]: reqs.filter((r) => statusOf(r) === s.id).length }), {});

  return (
    <div className="card-elev p-5">
      <div className="flex items-center gap-2 mb-3">
        <Scissors className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-xl">Embroidery queue</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="embroidery-status-summary">
        {STATUSES.map((s) => (
          <div key={s.id} className="px-1.5 py-2.5 rounded-xl bg-accent-motif text-center flex flex-col items-center justify-center min-h-[64px]" data-testid={`embroidery-count-${s.id}`}>
            <div className="text-[10px] tracking-wide text-motif-muted uppercase leading-tight break-words w-full">{s.label}</div>
            <div className="font-display text-lg text-primary-motif leading-none mt-1">{counts[s.id] || 0}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input className="input-motif text-sm py-2 flex-1 min-w-0" placeholder="Search by name or text…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="embroidery-search" />
        <select className="input-motif text-sm py-2 w-full sm:w-32" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="embroidery-filter">
          <option value="active">Active</option>
          <option value="all">All</option>
        </select>
      </div>
      <div className="space-y-2 max-h-[50vh] overflow-y-auto">
        {filtered.map((r) => (
          <div key={r.id} className="p-3 rounded-xl bg-accent-motif" data-testid={`embroidery-row-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="font-display text-lg text-motif">{r.item} · "<span className="tracking-widest">{r.text}</span>"</div>
                <div className="text-[10px] text-motif-muted">
                  {r.guest_name} · Table {r.table_name} ·{" "}
                  {Object.entries(r.options || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </div>
              </div>
              <Pill s={r.status} />
            </div>
            {next[statusOf(r)] && (
              <button onClick={() => update(r.id, next[statusOf(r)])} className="btn-primary text-xs px-3 py-1.5 mt-2 flex items-center gap-1" data-testid={`embroidery-advance-${r.id}`}>
                {next[statusOf(r)] === "completed" ? <Check className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                Mark {next[statusOf(r)].replace("_", " ")}
              </button>
            )}
            {onViewMap && (
              <button onClick={() => onViewMap(r)} className="btn-ghost text-xs mt-2 ml-2" data-testid={`embroidery-map-${r.id}`}>
                View on Map
              </button>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="text-motif-muted text-sm text-center py-4">No requests.</div>}
      </div>
    </div>
  );
}

function Pill({ s }) {
  const key = (s || "").toLowerCase();
  const colors = {
    pending: "var(--motif-text-muted)",
    in_progress: "#f59e0b",
    completed: "#10b981",
    cancelled: "var(--motif-text-muted)",
  };
  return (
    <span className="text-[10px] tracking-luxe px-2 py-1 rounded-full"
      style={{ background: "var(--motif-surface)", color: colors[key] || "var(--motif-text)" }}>
      {key.replace("_", " ")}
    </span>
  );
}
